import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

/** ---------------- i18n ---------------- */
const MESSAGES = {
    en_US: {
        tabs: { set: "Set Rules", apply: "Apply Rules" },
        loading: {
            assignments: "Loading assignments…",
            rules: "Loading rules…",
            students: "Loading students…",
            generic: "Loading…",
        },
        errors: {
            missingParams: "Missing required params.",
            auth401: "Authentication failed (401).",
            loadCourse: "Failed to load course data.",
            loadRules: "Failed to load rules.",
            loadColumns: "Failed to load gradebook columns.",
            loadAttempts: "Failed to load attempts.",
            loadStudents: "Failed to load students.",
            enterNonNegative: "Enter a non-negative number.",
        },
        headings: {
            assoc: "Assignment Late Penalty Associations",
            apply: "Apply Rules — Student × Assignment",
            student: "Your Late Penalties",
            total: "Total Late Penalties:",
        },
        tooltips: {
            total: "Sum of applicable penalties across assignments",
            updateLatePenalties:
                "Create/Find 'Late Penalties' column and write totals; also stores penalties snapshot",
            sortByAssignment: "Sort assignment columns by title",
            sortByStudent: "Sort by student name",
            sortBy: "Sort by {label}",
            clearRuleFor: "Clear rule for {title}",
            selectRuleFor: "Select rule for {title}",
        },
        buttons: {
            saveAll: "Save all",
            deleteAll: "Delete all",
            retry: "Retry",
            applyAndSave: "Apply & save",
            clearRule: "Clear rule",
            applyToAll: "Apply to all:",
            clearOption: "(clear)",
            sortAssignmentsAZ: "Sort Assignments A→Z",
            sortAssignmentsZA: "Sort Assignments Z→A",
            sortStudentsAZ: "Sort Students A→Z",
            sortStudentsZA: "Sort Students Z→A",
            updateLatePenalties: "Update Late Penalties",
            updating: "Updating…",
            save: "Save",
            saving: "Saving…",
            clear: "Clear",
        },
        table: {
            actions: "Actions",
            assignment: "Assignment",
            rule: "Rule",
            due: "Due",
            points: "Points",
            attempted: "Attempted",
            penalty: "Late Penalty",
            student: "Student",
            noAssignments: "No assignments found.",
            noStudents: "No students found.",
            columnsUnavailable: "columns unavailable",
            rulesUnavailable: "Rules unavailable",
            unknown: "Unknown",
        },
        meta: { points: "Points:", due: "Due:" },
        chips: { overridden: "Overridden" },
        attemptCard: {
            attempt: "Attempt:",
            status: "Status:",
            penalty: "Penalty:",
            noAttempt: "No attempt yet",
            overrideLabel: "Override:",
        },
        notices: {
            deletedOne: "Deleted rule (and any overrides/penalties on that item)",
            savedRule: "Saved rule",
            appliedAll: "Applied to all & saved",
            savedAll: "Saved all rules",
            deletedAll: "Deleted all rules (overrides & penalties) for this course",
            noStudentsOrCourse: "No students or course selected.",
            updatedLatePenalties:
                "Updated late penalties for {count} {studentWord} and stored snapshots.",
            syncFailed: "Sync failed:",
            bulkSaveFailed: "Bulk save failed:",
            saveAllFailed: "Save all failed:",
            deleteAllFailed: "Delete all failed:",
            saveFailed: "Save failed:",
            clearFailed: "Clear failed:",
            updateFailed: "Late Penalties update failed:",
        },
        penalty: {
            none: "No penalty",
            override: "override",
            pts: "pts",
            dash: "—",
            onTime: "on time",
            lateSuffix: "late",
            overrideApplied: "override applied",
            detailsHeading: "Late penalty details:",
            noneApplied: "No late penalties applied.",
        },
        footerErrors: { attempts: "Attempts:", columns: "Columns:" },
        names: { unnamed: "(Unnamed Student)" },
    },

    // AU English
    en_AU: {
        tabs: { set: "Set Rules", apply: "Apply Rules" },
        loading: {
            assignments: "Loading assignments…",
            rules: "Loading rules…",
            students: "Loading students…",
            generic: "Loading…",
        },
        errors: {
            missingParams: "Missing required params.",
            auth401: "Authentication failed (401).",
            loadCourse: "Failed to load course data.",
            loadRules: "Failed to load rules.",
            loadColumns: "Failed to load gradebook columns.",
            loadAttempts: "Failed to load attempts.",
            loadStudents: "Failed to load students.",
            enterNonNegative: "Enter a non-negative number.",
        },
        headings: {
            assoc: "Assignment Late Penalty Associations",
            apply: "Apply Rules — Student × Assignment",
            student: "Your Late Penalties",
            total: "Total Late Penalties:",
        },
        tooltips: {
            total: "Sum of applicable penalties across assignments",
            updateLatePenalties:
                "Create/Find 'Late Penalties' column and write totals; also stores penalties snapshot",
            sortByAssignment: "Sort assignment columns by title",
            sortByStudent: "Sort by student name",
            sortBy: "Sort by {label}",
            clearRuleFor: "Clear rule for {title}",
            selectRuleFor: "Select rule for {title}",
        },
        buttons: {
            saveAll: "Save all",
            deleteAll: "Delete all",
            retry: "Retry",
            applyAndSave: "Apply & save",
            clearRule: "Clear rule",
            applyToAll: "Apply to all:",
            clearOption: "(clear)",
            sortAssignmentsAZ: "Sort Assignments A→Z",
            sortAssignmentsZA: "Sort Assignments Z→A",
            sortStudentsAZ: "Sort Students A→Z",
            sortStudentsZA: "Sort Students Z→A",
            updateLatePenalties: "Update Late Penalties",
            updating: "Updating…",
            save: "Save",
            saving: "Saving…",
            clear: "Clear",
        },
        table: {
            actions: "Actions",
            assignment: "Assignment",
            rule: "Rule",
            due: "Due",
            points: "Points",
            attempted: "Attempted",
            penalty: "Late Penalty",
            student: "Student",
            noAssignments: "No assignments found.",
            noStudents: "No students found.",
            columnsUnavailable: "columns unavailable",
            rulesUnavailable: "Rules unavailable",
            unknown: "Unknown",
        },
        meta: { points: "Points:", due: "Due:" },
        chips: { overridden: "Overridden" },
        attemptCard: {
            attempt: "Attempt:",
            status: "Status:",
            penalty: "Penalty:",
            noAttempt: "No attempt yet",
            overrideLabel: "Override:",
        },
        notices: {
            deletedOne: "Deleted rule (and any overrides/penalties on that item)",
            savedRule: "Saved rule",
            appliedAll: "Applied to all & saved",
            savedAll: "Saved all rules",
            deletedAll: "Deleted all rules (overrides & penalties) for this course",
            noStudentsOrCourse: "No students or course selected.",
            updatedLatePenalties:
                "Updated late penalties for {count} {studentWord} and stored snapshots.",
            syncFailed: "Sync failed:",
            bulkSaveFailed: "Bulk save failed:",
            saveAllFailed: "Save all failed:",
            deleteAllFailed: "Delete all failed:",
            saveFailed: "Save failed:",
            clearFailed: "Clear failed:",
            updateFailed: "Late Penalties update failed:",
        },
        penalty: {
            none: "No penalty",
            override: "override",
            pts: "pts",
            dash: "—",
            onTime: "on time",
            lateSuffix: "late",
            overrideApplied: "override applied",
            detailsHeading: "Late penalty details:",
            noneApplied: "No late penalties applied.",
        },
        footerErrors: { attempts: "Attempts:", columns: "Columns:" },
        names: { unnamed: "(Unnamed Student)" },
    }
};

const fallbackLocale = "en_US";
const getMessages = (loc) => MESSAGES[loc] || MESSAGES[fallbackLocale];
const normLocale = (loc) => String(loc || fallbackLocale).replace("_", "-");
const tpl = (s, vars = {}) =>
    String(s).replace(/\{(\w+)\}/g, (_, k) => (vars[k] == null ? "" : String(vars[k])));

/** ---------------- Endpoints ---------------- */
const LPA_PATH = "/late-penalty-associations";
const RULES_PATH = "/late-penalty-rules";
const STORAGE_KEYS = { courseId: "courseId", activeTab: "uef-lpa-active-tab" };

// Helper: stable ID for your Lambda’s required `ID` field
const makeAssociationId = (courseId, contentItemId) => `${courseId}:${contentItemId}`;

/** ---------------- Backend Adapters ---------------- */
// Rules Catalog
// ✨ Add tenantId to inject x-tenant-id header
function makeRulesBackend(backEndUrl, tenantId) {
    const base = (backEndUrl || "").replace(/\/+$/, "");
    const http = axios.create({
        baseURL: base,
        timeout: 20000,
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...(tenantId ? { "x-tenant-id": tenantId } : {}),
        },
    });

    const listAll = async (limit = 200) => {
        if (!base) return [];
        const items = [];
        let nextKey = null;
        do {
            const res = await http.get(RULES_PATH, {
                params: { limit, ...(nextKey ? { nextKey: JSON.stringify(nextKey) } : {}) },
            });
            const page = res?.data || {};
            items.push(...(page.items || []));
            nextKey = page.nextKey || null;
        } while (nextKey);
        return items;
    };

    return { listAll };
}

// Associations (stores Rule + per-student Overrides + stored Penalties)
// ✨ Add tenantId to inject x-tenant-id header
function makeBackend(backEndUrl, tenantId) {
    const base = (backEndUrl || "").replace(/\/+$/, "");
    const http = axios.create({
        baseURL: base,
        timeout: 20000,
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...(tenantId ? { "x-tenant-id": tenantId } : {}),
        },
    });

    const getAllForCourse = async (courseId, limit = 200) => {
        const items = [];
        let nextKey = null;
        do {
            const res = await http.get(LPA_PATH, {
                params: { courseId, limit, ...(nextKey ? { nextKey: JSON.stringify(nextKey) } : {}) },
            });
            const page = res?.data || {};
            items.push(...(page.items || []));
            nextKey = page.nextKey || null;
        } while (nextKey);
        return items;
    };

    /** Load all associations as three maps: rules, overrides, penalties */
    const loadAssociations = async (courseId) => {
        if (!base) return { rules: {}, overridesBy: {}, penaltiesBy: {} };
        const items = await getAllForCourse(courseId);
        const rules = {};
        const overridesBy = {}; // { userId: { contentItemId: points } }
        const penaltiesBy = {}; // { userId: { contentItemId: points } }
        for (const it of items) {
            const cid = it?.ContentItemId;
            if (!cid) continue;
            if (it?.Rule !== undefined) rules[cid] = it.Rule;

            const ov = it?.Overrides || {};
            for (const [userId, pts] of Object.entries(ov)) {
                if (typeof pts !== "number") continue;
                if (!overridesBy[userId]) overridesBy[userId] = {};
                overridesBy[userId][cid] = pts;
            }

            const pen = it?.Penalties || {};
            for (const [userId, pts] of Object.entries(pen)) {
                if (typeof pts !== "number") continue;
                if (!penaltiesBy[userId]) penaltiesBy[userId] = {};
                penaltiesBy[userId][cid] = pts;
            }
        }
        return { rules, overridesBy, penaltiesBy, raw: items };
    };

    /** Helper: build payload preserving both Overrides and Penalties */
    const buildAssociationItem = (
        courseId,
        contentItemId,
        rule,
        existingOverrides = {},
        existingPenalties = {}
    ) => ({
        ID: makeAssociationId(courseId, contentItemId),
        CourseId: courseId,
        ContentItemId: contentItemId,
        Rule: rule,
        Status: "Active",
        Overrides: existingOverrides,
        Penalties: existingPenalties,
    });

    /** Upsert a single rule, preserving overrides & penalties on that content item */
    const upsertRule = async (
        courseId,
        contentItemId,
        rule,
        currentOverridesByUser = {},
        currentPenaltiesByUser = {}
    ) => {
        if (!base) return;
        const item = buildAssociationItem(
            courseId,
            contentItemId,
            rule,
            currentOverridesByUser,
            currentPenaltiesByUser
        );
        await http.post(LPA_PATH, [item]);
    };

    /** Bulk save rules, preserving overrides & penalties for each content id */
    const saveRulesBulk = async (
        courseId,
        nextRulesObj = {},
        currentOverridesMap = {},
        currentPenaltiesMap = {}
    ) => {
        if (!base) return;
        const payload = Object.entries(nextRulesObj).map(([contentItemId, rule]) =>
            buildAssociationItem(
                courseId,
                contentItemId,
                rule,
                currentOverridesMap[contentItemId] || {},
                currentPenaltiesMap[contentItemId] || {}
            )
        );
        if (!payload.length) return;
        await http.post(LPA_PATH, payload);
    };

    /** Delete rule (removes the entire association — overrides & penalties go with it) */
    const deleteRule = async (courseId, contentItemId) => {
        if (!base) return;
        await http.delete(LPA_PATH, { data: { CourseId: courseId, ContentItemId: contentItemId } });
    };

    /** Delete all associations for the course (rules + overrides + penalties) */
    const deleteAll = async (courseId) => {
        if (!base) return;
        const items = await getAllForCourse(courseId);
        await Promise.allSettled(
            (items || []).map((it) =>
                http.delete(LPA_PATH, { data: { CourseId: it.CourseId, ContentItemId: it.ContentItemId } })
            )
        );
    };

    /** Save a single override, preserving stored penalties */
    const saveOverride = async (
        courseId,
        contentItemId,
        userId,
        points,
        currentRule,
        existingOverridesForContent = {},
        existingPenaltiesForContent = {}
    ) => {
        if (!base) return;
        const mergedOv = { ...(existingOverridesForContent || {}) };
        mergedOv[userId] = Number(points);
        const item = buildAssociationItem(
            courseId,
            contentItemId,
            currentRule || "",
            mergedOv,
            existingPenaltiesForContent || {}
        );
        await http.post(LPA_PATH, [item]);
    };

    /** Clear a single override, preserving stored penalties */
    const deleteOverride = async (
        courseId,
        contentItemId,
        userId,
        currentRule,
        existingOverridesForContent = {},
        existingPenaltiesForContent = {}
    ) => {
        if (!base) return;
        const mergedOv = { ...(existingOverridesForContent || {}) };
        delete mergedOv[userId];
        const item = buildAssociationItem(
            courseId,
            contentItemId,
            currentRule || "",
            mergedOv,
            existingPenaltiesForContent || {}
        );
        await http.post(LPA_PATH, [item]);
    };

    /** NEW: Bulk save penalties by contentId -> { userId: points }, preserving overrides */
    const savePenaltiesBulk = async (
        courseId,
        penaltiesByContent = {}, // { contentId: { userId: points } }
        currentRulesMap = {}, // { contentId: ruleCode }
        currentOverridesMap = {}, // { contentId: { userId: points } }
        currentPenaltiesMap = {} // existing persisted { contentId: { userId: points } }
    ) => {
        if (!base) return;
        const payload = Object.entries(penaltiesByContent).map(([contentItemId, newPenMap]) => {
            const mergedPen = { ...(currentPenaltiesMap[contentItemId] || {}) };
            for (const [uid, pts] of Object.entries(newPenMap || {})) {
                if (Number.isFinite(pts) && pts >= 0) mergedPen[uid] = Number(pts);
            }
            return buildAssociationItem(
                courseId,
                contentItemId,
                currentRulesMap[contentItemId] || "",
                currentOverridesMap[contentItemId] || {},
                mergedPen
            );
        });
        if (!payload.length) return;
        await http.post(LPA_PATH, payload);
    };

    return {
        loadAssociations,
        upsertRule,
        saveRulesBulk,
        deleteRule,
        deleteAll,
        saveOverride,
        deleteOverride,
        savePenaltiesBulk, // NEW
    };
}

/** ---------------- Component ---------------- */
// ✨ Added tenantId prop (optional)
const LatePenaltyCourseDetails = ({ platformUrl, backEndUrl, locale = "en_US", tenantId }) => {
    const M = useMemo(() => getMessages(locale), [locale]);
    const localeTag = useMemo(() => normLocale(locale), [locale]);

    const location = useLocation();
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const token = queryParams.get("token");
    const courseIdFromQuery = queryParams.get("courseUuid") || "";
    const userIdParam = queryParams.get("uuid") || ""; // current user (Learn UUID)

    const [courseId, setCourseId] = useState(() => {
        return courseIdFromQuery || localStorage.getItem(STORAGE_KEYS.courseId) || "";
    });
    useEffect(() => {
        if (courseIdFromQuery && courseIdFromQuery !== localStorage.getItem(STORAGE_KEYS.courseId)) {
            localStorage.setItem(STORAGE_KEYS.courseId, courseIdFromQuery);
            setCourseId(courseIdFromQuery);
        }
    }, [courseIdFromQuery]);

    // Role/membership
    const [userMembership, setUserMembership] = useState(null); // { courseRoleId, id, userId, availability }
    const isInstructor = useMemo(() => {
        const r = (userMembership?.courseRoleId || "").toLowerCase();
        return r.includes("instructor");
    }, [userMembership]);

    // Tabs (instructor only): 'set' | 'apply'
    const [activeTab, setActiveTab] = useState(() => {
        return localStorage.getItem(STORAGE_KEYS.activeTab) || "set";
    });
    useEffect(() => {
        localStorage.setItem(STORAGE_KEYS.activeTab, activeTab);
    }, [activeTab]);

    const [assignments, setAssignments] = useState([]);
    const [status, setStatus] = useState({ loading: true, error: null });

    // Associations state
    const [assignmentRules, setAssignmentRules] = useState({}); // { contentId: ruleCode }

    // overridesByUser[userId][contentId] = points
    const [overridesByUser, setOverridesByUser] = useState({});

    // penaltiesByUser[userId][contentId] = points (persisted snapshot)
    const [penaltiesByUser, setPenaltiesByUser] = useState({});

    // convenient mirrors
    const overridesByContent = useMemo(() => {
        const byContent = {};
        for (const [userId, map] of Object.entries(overridesByUser)) {
            for (const [cid, pts] of Object.entries(map)) {
                if (!byContent[cid]) byContent[cid] = {};
                byContent[cid][userId] = pts;
            }
        }
        return byContent;
    }, [overridesByUser]);

    const penaltiesByContent = useMemo(() => {
        const byContent = {};
        for (const [userId, map] of Object.entries(penaltiesByUser)) {
            for (const [cid, pts] of Object.entries(map)) {
                if (!byContent[cid]) byContent[cid] = {};
                byContent[cid][userId] = pts;
            }
        }
        return byContent;
    }, [penaltiesByUser]);

    // Bulk selection state
    const [bulkRule, setBulkRule] = useState("");
    const [syncMsg, setSyncMsg] = useState("");

    // Sorting state (Set tab)
    const [sortKey, setSortKey] = useState(null); // 'title' | 'rule' | null
    const [sortDir, setSortDir] = useState("asc"); // 'asc' | 'desc'

    // Rules catalog state (for dropdown options)
    const [ruleOptions, setRuleOptions] = useState([]); // [{ value: Code, label: Label }]
    const [ruleLabelMap, setRuleLabelMap] = useState({}); // { Code: Label }
    const [rulesLoading, setRulesLoading] = useState(true);
    const [rulesError, setRulesError] = useState("");

    // Gradebook columns mapped by contentId -> { id, possible, due, gradebookCategoryId }
    const [colByContentId, setColByContentId] = useState({});
    const [columnsError, setColumnsError] = useState("");

    // Attempts indexed by userId (PK1) -> contentId -> attempt
    const [attemptsBy, setAttemptsBy] = useState({});
    const [attemptsError, setAttemptsError] = useState("");

    // --- Apply tab: students (course memberships) ---
    const [students, setStudents] = useState([]); // [{ id, userId, given, family, displayName }]
    const [studentsStatus, setStudentsStatus] = useState({ loading: false, error: "" });

    // Apply tab sorting
    const [studentSortDir, setStudentSortDir] = useState("asc");
    const [assignmentColSortDir, setAssignmentColSortDir] = useState("asc");

    // Button working state
    const [updatingPenalties, setUpdatingPenalties] = useState(false);

    // Student view sorting
    const [studentViewSortKey, setStudentViewSortKey] = useState("title");
    const [studentViewSortDir, setStudentViewSortDir] = useState("asc");

    // Track busy save/clear in Apply grid
    const [overridesBusy, setOverridesBusy] = useState({}); // key `${userId}:${contentId}`

    // Platform API clients
    const apiV1 = useMemo(() => {
        return axios.create({
            baseURL: `${platformUrl}/learn/api/public/v1`,
            timeout: 20000,
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
            },
        });
    }, [platformUrl, token]);

    const apiV2 = useMemo(() => {
        return axios.create({
            baseURL: `${platformUrl}/learn/api/public/v2`,
            timeout: 20000,
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
            },
        });
    }, [platformUrl, token]);

    // ✨ Pass tenantId through to backends
    const backend = useMemo(() => makeBackend(backEndUrl, tenantId), [backEndUrl, tenantId]);
    const rulesApi = useMemo(() => makeRulesBackend(backEndUrl, tenantId), [backEndUrl, tenantId]);

    /** Load current user's membership using uuid (userId) */
    useEffect(() => {
        let cancelled = false;
        async function loadMembership() {
            if (!token || !platformUrl || !courseId || !userIdParam) return;
            try {
                const res = await apiV1.get(
                    `/courses/${encodeURIComponent(courseId)}/users/uuid:${encodeURIComponent(userIdParam)}`,
                    { params: { fields: "id,userId,courseRoleId,availability" } }
                );
                if (!cancelled) setUserMembership(res?.data || null);
            } catch {
                if (!cancelled) setUserMembership(null);
            }
        }
        loadMembership();
        return () => {
            cancelled = true;
        };
    }, [apiV1, courseId, platformUrl, token, userIdParam]);

    /** Load assignments (contents) */
    useEffect(() => {
        let cancelled = false;
        async function load() {
            if (!platformUrl || !token || !courseId) {
                setStatus({ loading: false, error: M.errors.missingParams });
                return;
            }
            setStatus({ loading: true, error: null });
            try {
                const allContents = await fetchAllContents(apiV1, courseId);
                const ASSIGNMENT_HANDLERS = new Set([
                    "resource/x-bb-asmt-test-link",
                    "resource/x-bb-assignment",
                ]);
                const asmt = allContents
                    .filter((c) => ASSIGNMENT_HANDLERS.has(c?.contentHandler?.id))
                    .map((c) => ({
                        id: c.id,
                        title: c.title || c.name || "(Untitled)",
                        available: c?.availability?.available || M.table.unknown,
                    }))
                    .sort((a, b) => (a.title || "").localeCompare(b.title || ""));
                if (!cancelled) {
                    setAssignments(asmt);
                    setStatus({ loading: false, error: null });
                }
            } catch (err) {
                if (cancelled) return;
                const msg =
                    err?.response?.status === 401
                        ? M.errors.auth401
                        : err?.response?.data?.message || err?.message || M.errors.loadCourse;
                setStatus({ loading: false, error: msg });
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [apiV1, courseId, platformUrl, token, M]);

    /** Load rules catalog (Active only) */
    useEffect(() => {
        let cancelled = false;
        async function loadRulesCatalog() {
            setRulesLoading(true);
            setRulesError("");
            try {
                const items = await rulesApi.listAll();
                const active = (items || [])
                    .filter((r) => (r?.Status || "Active") === "Active")
                    .map((r) => ({
                        value: r?.Code ?? "",
                        label: r?.Label ?? "",
                        sort: Number.isFinite(Number(r?.SortOrder)) ? Number(r.SortOrder) : 0,
                    }))
                    .filter((r) => r.value && r.label)
                    .sort((a, b) => a.sort - b.sort || a.label.localeCompare(b.label));

                const map = Object.fromEntries(active.map((r) => [r.value, r.label]));
                if (!cancelled) {
                    setRuleOptions(active);
                    setRuleLabelMap(map);
                }
            } catch (e) {
                if (!cancelled) {
                    setRulesError(e?.response?.data?.message || e?.message || M.errors.loadRules);
                    setRuleOptions([]);
                    setRuleLabelMap({});
                }
            } finally {
                if (!cancelled) setRulesLoading(false);
            }
        }
        loadRulesCatalog();
        return () => {
            cancelled = true;
        };
    }, [rulesApi, M]);

    /** Hydrate existing associations (rules + overrides + penalties) */
    useEffect(() => {
        let cancelled = false;
        async function hydrate() {
            if (!courseId) return;
            try {
                const { rules, overridesBy, penaltiesBy } = await backend.loadAssociations(courseId);
                if (!cancelled) {
                    setAssignmentRules(rules || {});
                    setOverridesByUser(overridesBy || {});
                    setPenaltiesByUser(penaltiesBy || {});
                }
            } catch (e) {
                setSyncMsg(
                    `Could not load rules/overrides/penalties: ${e?.response?.data?.message || e.message || "error"}`
                );
                if (!cancelled) {
                    setAssignmentRules({});
                    setOverridesByUser({});
                    setPenaltiesByUser({});
                }
            }
        }
        hydrate();
        return () => {
            cancelled = true;
        };
    }, [backend, courseId]);

    /** Load gradebook columns & map by contentId -> (id, possible, due) */
    useEffect(() => {
        let cancelled = false;
        async function loadColumns() {
            if (!courseId) return;
            setColumnsError("");
            try {
                const params = { fields: "id,contentId,score,grading,name,gradebookCategoryId" };
                const res = await apiV2.get(`/courses/${encodeURIComponent(courseId)}/gradebook/columns`, {
                    params,
                });
                const rows = Array.isArray(res?.data?.results) ? res.data.results : [];
                const map = {};
                for (const c of rows) {
                    const contentId = c?.contentId;
                    if (!contentId) continue;
                    map[contentId] = {
                        id: c?.id,
                        possible: c?.score?.possible,
                        due: c?.grading?.due,
                        gradebookCategoryId: c?.gradebookCategoryId,
                    };
                }
                if (!cancelled) setColByContentId(map);
            } catch (e) {
                if (!cancelled) {
                    setColumnsError(e?.response?.data?.message || e?.message || M.errors.loadColumns);
                    setColByContentId({});
                }
            }
        }
        loadColumns();
        return () => {
            cancelled = true;
        };
    }, [apiV2, courseId, M]);

    /** Load attempts for every gradebook column we know about (paged) */
    useEffect(() => {
        let cancelled = false;
        async function loadAllAttempts() {
            setAttemptsError("");
            try {
                const entries = Object.entries(colByContentId || {});
                if (!entries.length) return;
                const byUser = {};
                for (const [contentId, meta] of entries) {
                    const colId = meta?.id;
                    if (!colId) continue;
                    let offset = 0;
                    const limit = 100;
                    while (true) {
                        const params = { limit, offset, fields: "id,userId,attemptDate,status,created" };
                        const res = await apiV2.get(
                            `/courses/${encodeURIComponent(courseId)}/gradebook/columns/${encodeURIComponent(colId)}/attempts`,
                            { params }
                        );
                        const attempts = Array.isArray(res?.data?.results) ? res.data.results : [];
                        for (const a of attempts) {
                            const uid = a?.userId;
                            if (!uid) continue;
                            if (!byUser[uid]) byUser[uid] = {};
                            byUser[uid][contentId] = {
                                attemptDate: a?.attemptDate || a?.created || null,
                                status: a?.status || a?.attemptStatus || "",
                            };
                        }
                        if (attempts.length < limit) break;
                        offset += limit;
                    }
                }
                if (!cancelled) setAttemptsBy(byUser);
            } catch (e) {
                if (!cancelled) {
                    setAttemptsBy({});
                    setAttemptsError(e?.response?.data?.message || e?.message || M.errors.loadAttempts);
                }
            }
        }
        loadAllAttempts();
        return () => {
            cancelled = true;
        };
    }, [apiV2, courseId, colByContentId, M]);

    /** Load students (course memberships) */
    useEffect(() => {
        let cancelled = false;
        async function loadStudents() {
            if (!courseId || !platformUrl || !token) return;
            setStudentsStatus({ loading: true, error: "" });
            try {
                const memberships = await fetchAllCourseUsers(apiV1, courseId, { expandUser: true });
                let list = [];
                if (memberships?.length) {
                    list = memberships
                        .filter((m) => String(m?.courseRoleId || "").toLowerCase().includes("student"))
                        .map((m) => {
                            const u = m?.user || {};
                            const name = u?.name || {};
                            return {
                                id: m?.id || `${m?.userId || ""}`,
                                userId: m?.userId || "",
                                given: name?.given || "",
                                family: name?.family || "",
                                displayName:
                                    u?.displayName || `${name?.given || ""} ${name?.family || ""}`.trim(),
                            };
                        });

                    const needsLookup = list.filter(
                        (s) => !s.given && !s.family && !s.displayName && s.userId
                    );
                    if (needsLookup.length) {
                        const looked = await bestEffortUserLookups(
                            apiV1,
                            needsLookup.map((s) => s.userId)
                        );
                        const nameMap = Object.fromEntries(
                            looked.map((u) => [
                                u.id,
                                {
                                    given: u?.name?.given || "",
                                    family: u?.name?.family || "",
                                    displayName: u?.displayName || "",
                                },
                            ])
                        );
                        list = list.map((s) => {
                            const nm = nameMap[s.userId] || {};
                            return {
                                ...s,
                                given: s.given || nm.given || "",
                                family: s.family || nm.family || "",
                                displayName:
                                    s.displayName ||
                                    nm.displayName ||
                                    `${nm.given || ""} ${nm.family || ""}`.trim(),
                            };
                        });
                    }
                }
                if (!cancelled) setStudents(list);
                if (!cancelled) setStudentsStatus({ loading: false, error: "" });
            } catch (e) {
                if (!cancelled) {
                    setStudents([]);
                    setStudentsStatus({
                        loading: false,
                        error: e?.response?.data?.message || e?.message || M.errors.loadStudents,
                    });
                }
            }
        }
        loadStudents();
        return () => {
            cancelled = true;
        };
    }, [apiV1, courseId, platformUrl, token, M]);

    const notify = (msg) => {
        setSyncMsg(msg);
        window.clearTimeout(notify._t);
        notify._t = window.setTimeout(() => setSyncMsg(""), 4000);
    };

    const getRuleLabel = (code) => ruleLabelMap[code ?? ""] ?? "";

    /** Save/clear a rule; keep existing overrides & penalties for that content item */
    const handleRuleChange = async (contentItemId, value) => {
        setAssignmentRules((prev) => ({ ...prev, [contentItemId]: value }));
        try {
            const existingOverridesForContent = overridesByContent[contentItemId] || {};
            const existingPenaltiesForContent = penaltiesByContent[contentItemId] || {};
            if (!value) {
                await backend.deleteRule(courseId, contentItemId);
                notify(M.notices.deletedOne);
                // purge overrides & penalties for this content locally
                setOverridesByUser((prev) => {
                    const copy = {};
                    for (const [uid, map] of Object.entries(prev)) {
                        const m = { ...map };
                        delete m[contentItemId];
                        copy[uid] = m;
                    }
                    return copy;
                });
                setPenaltiesByUser((prev) => {
                    const copy = {};
                    for (const [uid, map] of Object.entries(prev)) {
                        const m = { ...map };
                        delete m[contentItemId];
                        copy[uid] = m;
                    }
                    return copy;
                });
            } else {
                await backend.upsertRule(
                    courseId,
                    contentItemId,
                    value,
                    existingOverridesForContent,
                    existingPenaltiesForContent
                );
                notify(M.notices.savedRule);
            }
        } catch (e) {
            notify(`${M.notices.syncFailed} ${e?.response?.data?.message || e.message || "error"}`);
        }
    };

    /** Bulk apply rules; preserve overrides & penalties for each content id */
    const handleApplyAll = async () => {
        if (!bulkRule) return;
        if (assignments.length === 0) return;
        const next = {};
        for (const a of assignments) next[a.id] = bulkRule;
        setAssignmentRules(next);
        try {
            await backend.saveRulesBulk(
                courseId,
                next,
                // map contentId -> overrides map for that content
                Object.fromEntries(assignments.map((a) => [a.id, overridesByContent[a.id] || {}])),
                // map contentId -> penalties map for that content
                Object.fromEntries(assignments.map((a) => [a.id, penaltiesByContent[a.id] || {}]))
            );
            notify(M.notices.appliedAll);
        } catch (e) {
            notify(`${M.notices.bulkSaveFailed} ${e?.response?.data?.message || e.message || "error"}`);
        }
    };

    const handleSaveAll = async () => {
        try {
            await backend.saveRulesBulk(
                courseId,
                assignmentRules || {},
                Object.fromEntries(
                    Object.keys(assignmentRules || {}).map((cid) => [cid, overridesByContent[cid] || {}])
                ),
                Object.fromEntries(
                    Object.keys(assignmentRules || {}).map((cid) => [cid, penaltiesByContent[cid] || {}])
                )
            );
            notify(M.notices.savedAll);
        } catch (e) {
            notify(`${M.notices.saveAllFailed} ${e?.response?.data?.message || e.message || "error"}`);
        }
    };

    const handleDeleteAll = async () => {
        setAssignmentRules({});
        setBulkRule("");
        try {
            await backend.deleteAll(courseId);
            setOverridesByUser({});
            setPenaltiesByUser({});
            notify(M.notices.deletedAll);
        } catch (e) {
            notify(`${M.notices.deleteAllFailed} ${e?.response?.data?.message || e?.message || "error"}`);
        }
    };

    /** Short month + day + short time (locale-aware) */
    function formatShort(iso) {
        if (!iso) return M.penalty.dash;
        try {
            const d = new Date(iso);
            if (Number.isNaN(d.getTime())) return M.penalty.dash;
            const s = d.toLocaleString(localeTag, {
                month: "short",
                day: "2-digit",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
            });
            return s.replace(",", "").replace(" AM", "a").replace(" PM", "p");
        } catch {
            return M.penalty.dash;
        }
    }

    function lateAmountText(dueIso, attemptIso) {
        if (!dueIso || !attemptIso) return null;
        const due = new Date(dueIso);
        const when = new Date(attemptIso);
        if (Number.isNaN(due.getTime()) || Number.isNaN(when.getTime())) return null;

        const ms = when.getTime() - due.getTime();
        if (ms <= 0) return { isLate: false, text: M.penalty.onTime };

        let totalMinutes = Math.floor(ms / 60000);
        const days = Math.floor(totalMinutes / (60 * 24));
        totalMinutes -= days * 60 * 24;
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        const parts = [];
        if (days > 0) parts.push(`${days} ${days === 1 ? "day" : "days"}`);
        if (hours > 0) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
        if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);

        const text = parts.length > 0 ? `${parts.join(", ")} ${M.penalty.lateSuffix}` : M.penalty.onTime;
        return { isLate: true, text };
    }

    const friendlyDate = (iso) => formatShort(iso);

    /** Apply an override (if present) to a computed penalty object */
    function withOverride(penaltyObj, overridePoints) {
        if (typeof overridePoints !== "number" || overridePoints < 0) return penaltyObj;
        if (!penaltyObj) {
            return {
                applies: overridePoints > 0,
                type: "override",
                amount: overridePoints,
                computed: round2(overridePoints),
            };
        }
        return { ...penaltyObj, applies: overridePoints > 0, computed: round2(overridePoints), overridden: true };
    }

    /** Build feedback notes (HTML) for a student */
    function buildPenaltyNotesForStudent(userId) {
        const lines = [];
        for (const a of assignments) {
            const ruleCode = assignmentRules[a.id] ?? "";
            const ruleLabel = getRuleLabel(ruleCode);
            const colMeta = colByContentId[a.id] || {};
            const attempt = (attemptsBy[userId] || {})[a.id] || null;

            const base = computePenalty(colMeta?.possible, colMeta?.due, attempt?.attemptDate, ruleLabel);
            const ovPts = overridesByUser?.[userId]?.[a.id];
            const pObj = withOverride(base, ovPts);

            if (pObj?.applies && Number.isFinite(pObj.computed) && pObj.computed > 0 && attempt?.attemptDate) {
                const whenTxt = friendlyDate(attempt.attemptDate);
                const late = lateAmountText(colMeta?.due, attempt?.attemptDate);
                const lostPts = pObj.computed;
                const ovTag = typeof ovPts === "number" ? ` (${M.penalty.overrideApplied})` : "";
                const piece =
                    `<p><strong>${a.title}:</strong> ` +
                    `You submitted on ${whenTxt} ` +
                    (late?.isLate ? `but were ${late.text} ` : `${M.penalty.onTime} `) +
                    `so you lost ${lostPts} ${M.penalty.pts}${ovTag}.</p>`;
                lines.push(piece);
            }
        }
        if (!lines.length) return M.penalty.noneApplied;
        return `<h6>${M.penalty.detailsHeading}</h6>${lines.join("")}`;
    }

    /** Create/Find "Late Penalties" column, update scores and persist penalties */
    const handleUpdateLatePenalties = async () => {
        if (!courseId || !students.length) {
            notify(M.notices.noStudentsOrCourse);
            return;
        }
        setUpdatingPenalties(true);
        try {
            // carry over category id from an existing assignment column
            const candidateCategoryId =
                assignments.map((a) => colByContentId[a.id]?.gradebookCategoryId).find(Boolean);

            const colId = await getOrCreateLatePenaltiesColumn(apiV2, courseId, candidateCategoryId);

            // Collect penalties to persist by content
            const toStoreByContent = {}; // { contentId: { userId: points } }

            let updated = 0;
            for (const s of students) {
                // Patch gradebook total
                const total = computeTotalPenaltyForStudent(s.userId);
                const scoreToSet = -1 * total;
                const notes = buildPenaltyNotesForStudent(s.userId);
                await apiV2.patch(
                    `/courses/${encodeURIComponent(courseId)}/gradebook/columns/${encodeURIComponent(colId)}/users/${encodeURIComponent(s.userId)}`,
                    { score: scoreToSet, feedback: notes }
                );
                updated++;

                // Build per-assignment penalties for this student
                for (const a of assignments) {
                    const colMeta = colByContentId[a.id] || {};
                    const attempt = (attemptsBy[s.userId] || {})[a.id] || null;
                    const ruleCode = assignmentRules[a.id] ?? "";
                    const ruleLabel = getRuleLabel(ruleCode);
                    const base = computePenalty(colMeta?.possible, colMeta?.due, attempt?.attemptDate, ruleLabel);
                    const ovPts = overridesByUser?.[s.userId]?.[a.id];
                    const p = withOverride(base, ovPts);
                    const val = Number.isFinite(p?.computed) && p?.applies ? round2(p.computed) : 0;

                    // Only store positive penalties to avoid bloating with zeros
                    if (val > 0) {
                        if (!toStoreByContent[a.id]) toStoreByContent[a.id] = {};
                        toStoreByContent[a.id][s.userId] = val;
                    }
                }
            }

            // Persist penalties snapshot (merge, preserve overrides)
            await backend.savePenaltiesBulk(
                courseId,
                toStoreByContent,
                assignmentRules,
                overridesByContent,
                penaltiesByContent
            );

            // Update in-memory penaltiesByUser with what we just saved
            setPenaltiesByUser((prev) => {
                const next = { ...(prev || {}) };
                for (const [cid, map] of Object.entries(toStoreByContent)) {
                    for (const [uid, pts] of Object.entries(map)) {
                        if (!next[uid]) next[uid] = {};
                        next[uid] = { ...next[uid], [cid]: pts };
                    }
                }
                return next;
            });

            const studentWord = updated === 1 ? "student" : "students";
            notify(tpl(M.notices.updatedLatePenalties, { count: updated, studentWord }));
        } catch (e) {
            notify(`${M.notices.updateFailed} ${e?.response?.data?.message || e?.message || "error"}`);
        } finally {
            setUpdatingPenalties(false);
        }
    };

    /** Save/Clear per-cell override on the association record (preserve penalties) */
    const handleSaveOverride = async (contentItemId, userId, points) => {
        const busyKey = `${userId}:${contentItemId}`;
        setOverridesBusy((b) => ({ ...b, [busyKey]: true }));
        try {
            const currentRule = assignmentRules[contentItemId] || "";
            const existingForContent = overridesByContent[contentItemId] || {};
            const existingPenForContent = penaltiesByContent[contentItemId] || {};
            await backend.saveOverride(
                courseId,
                contentItemId,
                userId,
                round2(points),
                currentRule,
                existingForContent,
                existingPenForContent
            );
            setOverridesByUser((prev) => {
                const next = { ...(prev || {}) };
                if (!next[userId]) next[userId] = {};
                next[userId] = { ...next[userId], [contentItemId]: round2(points) };
                return next;
            });
            notify("Override saved.");
        } catch (e) {
            notify(`${M.notices.saveFailed} ${e?.response?.data?.message || e?.message || "error"}`);
        } finally {
            setOverridesBusy((b) => {
                const n = { ...b };
                delete n[busyKey];
                return n;
            });
        }
    };

    const handleClearOverride = async (contentItemId, userId) => {
        const busyKey = `${userId}:${contentItemId}`;
        setOverridesBusy((b) => ({ ...b, [busyKey]: true }));
        try {
            const currentRule = assignmentRules[contentItemId] || "";
            const existingForContent = overridesByContent[contentItemId] || {};
            const existingPenForContent = penaltiesByContent[contentItemId] || {};
            await backend.deleteOverride(
                courseId,
                contentItemId,
                userId,
                currentRule,
                existingForContent,
                existingPenForContent
            );
            setOverridesByUser((prev) => {
                const copy = { ...(prev || {}) };
                const map = { ...(copy[userId] || {}) };
                delete map[contentItemId];
                copy[userId] = map;
                return copy;
            });
            notify("Override cleared.");
        } catch (e) {
            notify(`${M.notices.clearFailed} ${e?.response?.data?.message || e?.message || "error"}`);
        } finally {
            setOverridesBusy((b) => {
                const n = { ...b };
                delete n[busyKey];
                return n;
            });
        }
    };

    /** Sorting helpers (Set tab) */
    const toggleSort = (key) => {
        setSortKey((prevKey) => {
            if (prevKey === key) {
                setSortDir((prevDir) => (prevDir === "asc" ? "desc" : "asc"));
                return prevKey;
            } else {
                setSortDir("asc");
                return key;
            }
        });
    };

    const sortedAssignments = useMemo(() => {
        const list = assignments.slice();
        if (!sortKey) return list;
        const dir = sortDir === "asc" ? 1 : -1;

        if (sortKey === "title") {
            list.sort((a, b) => {
                const tA = (a.title || "").toLocaleLowerCase();
                const tB = (b.title || "").toLocaleLowerCase();
                if (tA < tB) return -1 * dir;
                if (tA > tB) return 1 * dir;
                const rA = getRuleLabel(assignmentRules[a.id] ?? "");
                const rB = getRuleLabel(assignmentRules[b.id] ?? "");
                return rA.localeCompare(rB) * dir;
            });
        } else if (sortKey === "rule") {
            list.sort((a, b) => {
                const rA = getRuleLabel(assignmentRules[a.id] ?? "");
                const rB = getRuleLabel(assignmentRules[b.id] ?? "");
                const cmp = rA.localeCompare(rB);
                if (cmp !== 0) return cmp * dir;
                const tA = (a.title || "").toLocaleLowerCase();
                const tB = (b.title || "").toLocaleLowerCase();
                if (tA < tB) return -1 * dir;
                if (tA > tB) return 1 * dir;
                return 0;
            });
        }
        return list;
    }, [assignments, assignmentRules, sortKey, sortDir, ruleLabelMap]);

    // Apply tab: sorted students and sorted assignment columns
    const sortedStudents = useMemo(() => {
        const list = students.slice();
        const dir = studentSortDir === "asc" ? 1 : -1;
        list.sort((a, b) => {
            const aLast = (a.family || "").toLocaleLowerCase();
            const bLast = (b.family || "").toLocaleLowerCase();
            if (aLast < bLast) return -1 * dir;
            if (aLast > bLast) return 1 * dir;
            const aFirst = (a.given || "").toLocaleLowerCase();
            const bFirst = (b.given || "").toLocaleLowerCase();
            if (aFirst < bFirst) return -1 * dir;
            if (aFirst > bFirst) return 1 * dir;
            return 0;
        });
        return list;
    }, [students, studentSortDir]);

    const sortedAssignmentsForApply = useMemo(() => {
        const list = assignments.slice();
        const dir = assignmentColSortDir === "asc" ? 1 : -1;
        list.sort((a, b) => {
            const tA = (a.title || "").toLocaleLowerCase();
            const tB = (b.title || "").toLocaleLowerCase();
            if (tA < tB) return -1 * dir;
            if (tA > tB) return 1 * dir;
            return 0;
        });
        return list;
    }, [assignments, assignmentColSortDir]);

    /** Prefer UUID if keyed, else PK1 from membership */
    const currentUserAttemptKey = useMemo(() => {
        if (userIdParam && attemptsBy[userIdParam]) return userIdParam;
        const pk1 = userMembership?.userId || "";
        if (pk1 && attemptsBy[pk1]) return pk1;
        return userIdParam || (userMembership?.userId || "");
    }, [attemptsBy, userIdParam, userMembership]);

    /** Student view rows */
    const studentRows = useMemo(() => {
        if (!currentUserAttemptKey) return [];
        return assignments.map((a) => {
            const meta = colByContentId[a.id] || {};
            const attempt = (attemptsBy[currentUserAttemptKey] || {})[a.id] || null;
            const ruleCode = assignmentRules[a.id] ?? "";
            const ruleLabel = getRuleLabel(ruleCode);
            const base = computePenalty(meta?.possible, meta?.due, attempt?.attemptDate, ruleLabel);
            const ovPts = overridesByUser?.[currentUserAttemptKey]?.[a.id];
            const penaltyObj = withOverride(base, ovPts);
            return {
                id: a.id,
                title: a.title,
                rule: ruleLabel || "",
                due: meta?.due || null,
                possible: Number.isFinite(meta?.possible) ? meta.possible : null,
                attempt: attempt?.attemptDate || null,
                penalty: penaltyObj,
            };
        });
    }, [assignments, colByContentId, attemptsBy, assignmentRules, currentUserAttemptKey, ruleLabelMap, overridesByUser]);

    /** Sort student view rows */
    const sortedStudentRows = useMemo(() => {
        const rows = studentRows.slice();
        const dir = studentViewSortDir === "asc" ? 1 : -1;
        rows.sort((a, b) => {
            const key = studentViewSortKey;
            const va = a[key];
            const vb = b[key];
            if (key === "title" || key === "rule") {
                const ta = (va || "").toLocaleLowerCase();
                const tb = (vb || "").toLocaleLowerCase();
                if (ta < tb) return -1 * dir;
                if (ta > tb) return 1 * dir;
                return 0;
            }
            if (key === "due" || key === "attempt") {
                const da = va ? new Date(va).getTime() : 0;
                const db = vb ? new Date(vb).getTime() : 0;
                return (da - db) * dir;
            }
            if (key === "possible") {
                const pa = Number.isFinite(va) ? va : -Infinity;
                const pb = Number.isFinite(vb) ? vb : -Infinity;
                if (pa < pb) return -1 * dir;
                if (pa > pb) return 1 * dir;
                return 0;
            }
            if (key === "penalty") {
                const pa = a.penalty?.computed ?? -Infinity;
                const pb = b.penalty?.computed ?? -Infinity;
                if (pa < pb) return -1 * dir;
                if (pa > pb) return 1 * dir;
                return 0;
            }
            return 0;
        });
        return rows;
    }, [studentRows, studentViewSortKey, studentViewSortDir]);

    const ariaSort = (key) => (sortKey === key ? (sortDir === "asc" ? "ascending" : "descending") : "none");
    const sortGlyph = (key) => (sortKey !== key ? "↕" : sortDir === "asc" ? "▲" : "▼");
    const colSortGlyph = assignmentColSortDir === "asc" ? "▲" : "▼";
    const studentSortGlyph = studentViewSortDir === "asc" ? "▲" : "▼";

    /** Render gates */
    if (status.loading) {
        return <div className="bb-container" style={{ padding: 16 }}>{M.loading.assignments}</div>;
    }
    if (status.error) {
        return <div className="bb-container" style={{ padding: 16, color: "#b00020" }}>{status.error}</div>;
    }

    /** If not instructor, render the Student view */
    if (!isInstructor) {
        const totalPenalty = computeTotalPenaltyForStudent(currentUserAttemptKey);
        return (
            <div className="bb-container" style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
                <section className="bb-card" style={cardStyle}>
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                        <h2 style={h2Style}>{M.headings.student}</h2>
                        <div style={{ fontWeight: 700, fontSize: 16 }}>
                            {M.headings.total} <span title={M.tooltips.total}>-{totalPenalty} {M.penalty.pts}</span>
                        </div>
                    </div>

                    <div style={{ width: "100%", overflow: "auto" }}>
                        <table className="bb-table" style={listTableStyle}>
                            <thead>
                            <tr>
                                {studentHeaderCell(M.table.assignment, "title", { studentViewSortKey, studentViewSortDir, setStudentViewSortKey, setStudentViewSortDir, M })}
                                {studentHeaderCell(M.table.rule, "rule", { studentViewSortKey, studentViewSortDir, setStudentViewSortKey, setStudentViewSortDir, M })}
                                {studentHeaderCell(M.table.due, "due", { studentViewSortKey, studentViewSortDir, setStudentViewSortKey, setStudentViewSortDir, M })}
                                {studentHeaderCell(M.table.points, "possible", { studentViewSortKey, studentViewSortDir, setStudentViewSortKey, setStudentViewSortDir, M })}
                                {studentHeaderCell(M.table.attempted, "attempt", { studentViewSortKey, studentViewSortDir, setStudentViewSortKey, setStudentViewSortDir, M })}
                                {studentHeaderCell(M.table.penalty, "penalty", { studentViewSortKey, studentViewSortDir, setStudentViewSortKey, setStudentViewSortDir, M })}
                            </tr>
                            </thead>
                            <tbody>
                            {sortedStudentRows.length === 0 ? (
                                <tr><td colSpan={6} style={{ textAlign: "center" }}>{M.table.noAssignments}</td></tr>
                            ) : (
                                sortedStudentRows.map((r) => (
                                    <tr key={r.id}>
                                        <td>{r.title}</td>
                                        <td>{r.rule || M.penalty.dash}</td>
                                        <td>{formatDue(r.due, localeTag)}</td>
                                        <td>{Number.isFinite(r.possible) ? r.possible : M.penalty.dash}</td>
                                        <td>{formatDue(r.attempt, localeTag)}</td>
                                        <td>{formatPenalty(r.penalty, M)}</td>
                                    </tr>
                                ))
                            )}
                            </tbody>
                        </table>
                    </div>

                    {(attemptsError || columnsError) && (
                        <div style={{ marginTop: 12 }}>
                            {attemptsError && <div style={{ color: "#b91c1c", fontSize: 12 }}>{M.footerErrors.attempts} {attemptsError}</div>}
                            {columnsError && <div style={{ color: "#b91c1c", fontSize: 12 }}>{M.footerErrors.columns} {columnsError}</div>}
                        </div>
                    )}
                </section>
            </div>
        );
    }

    /** Instructor view */
    return (
        <div className="bb-container" style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
            {/* Tabs */}
            <div role="tablist" aria-label="Late Penalty Tabs" style={tabsRowStyle}>
                <button
                    role="tab"
                    aria-selected={activeTab === "set"}
                    aria-controls="panel-set"
                    id="tab-set"
                    onClick={() => setActiveTab("set")}
                    style={{ ...tabBtn, ...(activeTab === "set" ? tabBtnActive : {}) }}
                >
                    {M.tabs.set}
                </button>
                <button
                    role="tab"
                    aria-selected={activeTab === "apply"}
                    aria-controls="panel-apply"
                    id="tab-apply"
                    onClick={() => setActiveTab("apply")}
                    style={{ ...tabBtn, ...(activeTab === "apply" ? tabBtnActive : {}) }}
                >
                    {M.tabs.apply}
                </button>
            </div>

            {/* Panels */}
            {activeTab === "set" ? (
                <section id="panel-set" role="tabpanel" aria-labelledby="tab-set" className="bb-card" style={cardStyle}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                        <h2 style={h2Style}>{M.headings.assoc}</h2>
                        <div style={{ display: "flex", gap: 8 }}>
                            <button type="button" onClick={handleSaveAll} style={primaryBtn}>{M.buttons.saveAll}</button>
                            <button type="button" onClick={handleDeleteAll} style={dangerBtn}>{M.buttons.deleteAll}</button>
                        </div>
                    </div>

                    {/* Bulk apply row */}
                    <div style={{ marginBottom: 12 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                            <label>{M.buttons.applyToAll}</label>

                            {rulesLoading ? (
                                <span style={{ fontSize: 12, color: "#666" }}>{M.loading.rules}</span>
                            ) : rulesError ? (
                                <>
                                    <span style={{ fontSize: 12, color: "#b91c1c" }}>{rulesError}</span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setRulesLoading(true);
                                            setTimeout(() => setRulesLoading(false), 0);
                                        }}
                                        style={linkBtn}
                                    >
                                        {M.buttons.retry}
                                    </button>
                                </>
                            ) : (
                                <select value={bulkRule} onChange={(e) => setBulkRule(e.target.value)} style={selectStyle}>
                                    <option value="">{M.buttons.clearOption}</option>
                                    {ruleOptions.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            )}

                            <button type="button" onClick={handleApplyAll} style={primaryBtn} disabled={!bulkRule || rulesLoading}>
                                {M.buttons.applyAndSave}
                            </button>
                        </div>
                    </div>

                    {syncMsg && <div style={toastStyle} role="status" aria-live="polite">{syncMsg}</div>}

                    <table className="bb-table" style={listTableStyle}>
                        <thead>
                        <tr>
                            <th style={{ width: "10%" }}>{M.table.actions}</th>

                            <th
                                style={{ width: "45%", cursor: "pointer", userSelect: "none" }}
                                role="button"
                                tabIndex={0}
                                aria-sort={ariaSort("title")}
                                onClick={() => toggleSort("title")}
                                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggleSort("title")}
                                title={tpl(M.tooltips.sortBy, { label: M.table.assignment })}
                            >
                                {M.table.assignment} <span style={sortIconStyle}>{sortGlyph("title")}</span>
                            </th>

                            <th
                                style={{ width: "45%", cursor: "pointer", userSelect: "none" }}
                                role="button"
                                tabIndex={0}
                                aria-sort={ariaSort("rule")}
                                onClick={() => toggleSort("rule")}
                                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggleSort("rule")}
                                title={tpl(M.tooltips.sortBy, { label: M.table.rule })}
                            >
                                {M.table.rule} <span style={sortIconStyle}>{sortGlyph("rule")}</span>
                            </th>
                        </tr>
                        </thead>

                        <tbody>
                        {sortedAssignments.length === 0 ? (
                            <tr><td colSpan={3} style={{ textAlign: "center" }}>{M.table.noAssignments}</td></tr>
                        ) : sortedAssignments.map((a) => {
                            const selected = assignmentRules[a.id] ?? "";
                            const col = colByContentId[a.id];
                            const possible = Number.isFinite(col?.possible) ? col.possible : null;
                            const dueIso = col?.due || null;
                            return (
                                <tr key={a.id}>
                                    <td style={{ textAlign: "center" }}>
                                        <button
                                            type="button"
                                            onClick={() => handleRuleChange(a.id, "")}
                                            style={iconBtn}
                                            title={tpl(M.tooltips.clearRuleFor, { title: a.title })}
                                            aria-label={tpl(M.tooltips.clearRuleFor, { title: a.title })}
                                        >
                                            ❌
                                        </button>
                                    </td>
                                    <td>
                                        <div style={{ fontWeight: 600 }}>{a.title}</div>
                                        <div style={{ fontSize: 12, color: "#666" }}>
                                            {a.available || M.table.unknown}
                                        </div>
                                        <div style={{ fontSize: 12, color: "#444", marginTop: 4 }}>
                                            <span><strong>{M.meta.points}</strong> {possible ?? M.penalty.dash}</span>
                                            <span style={{ margin: "0 8px" }}>·</span>
                                            <span><strong>{M.meta.due}</strong> {formatDue(dueIso, localeTag)}</span>
                                            {columnsError && (
                                                <>
                                                    <span style={{ margin: "0 8px" }}>·</span>
                                                    <span style={{ color: "#b91c1c" }} title={columnsError}>{M.table.columnsUnavailable}</span>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        {rulesLoading ? (
                                            <span style={{ fontSize: 12, color: "#666" }}>{M.loading.generic}</span>
                                        ) : rulesError ? (
                                            <span style={{ fontSize: 12, color: "#b91c1c" }}>{M.table.rulesUnavailable}</span>
                                        ) : (
                                            <select
                                                value={selected}
                                                onChange={(e) => handleRuleChange(a.id, e.target.value)}
                                                style={selectStyle}
                                                aria-label={tpl(M.tooltips.selectRuleFor, { title: a.title })}
                                            >
                                                <option value="">{M.buttons.clearOption}</option>
                                                {ruleOptions.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </section>
            ) : (
                // --- APPLY RULES TAB ---
                <section id="panel-apply" role="tabpanel" aria-labelledby="tab-apply" className="bb-card" style={{ ...cardStyle, padding: 0 }}>
                    <div style={{ padding: 16, borderBottom: "1px solid #e5e7eb", display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
                        <h2 style={h2Style}>{M.headings.apply}</h2>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                            <button
                                type="button"
                                onClick={() => setAssignmentColSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                                style={secondaryBtn}
                                title={M.tooltips.sortByAssignment}
                            >
                                {assignmentColSortDir === "asc" ? M.buttons.sortAssignmentsAZ : M.buttons.sortAssignmentsZA} {colSortGlyph}
                            </button>
                            <button
                                type="button"
                                onClick={() => setStudentSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                                style={secondaryBtn}
                                title={M.tooltips.sortByStudent}
                            >
                                {studentSortDir === "asc" ? M.buttons.sortStudentsAZ : M.buttons.sortStudentsZA} {studentSortGlyph}
                            </button>
                            <button
                                type="button"
                                onClick={handleUpdateLatePenalties}
                                style={{ ...primaryBtn, opacity: updatingPenalties ? 0.7 : 1 }}
                                disabled={updatingPenalties || studentsStatus.loading || !students.length}
                                title={M.tooltips.updateLatePenalties}
                            >
                                {updatingPenalties ? M.buttons.updating : M.buttons.updateLatePenalties}
                            </button>
                        </div>
                    </div>

                    {(studentsStatus.loading || !students.length) && (
                        <div style={{ padding: 12 }}>
                            {studentsStatus.loading ? M.loading.students : M.table.noStudents}
                            {studentsStatus.error && (
                                <div style={{ marginTop: 6, color: "#b91c1c", fontSize: 12 }}>{studentsStatus.error}</div>
                            )}
                        </div>
                    )}

                    {!!students.length && (
                        <div style={{ width: "100%", overflow: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                                <thead>
                                <tr>
                                    <th
                                        style={{ position: "sticky", left: 0, zIndex: 3, background: "#f9fafb", boxShadow: "2px 0 0 #e5e7eb", minWidth: 240, padding: "8px 10px", borderBottom: "1px solid #e5e7eb", cursor: "pointer", textAlign: "left", whiteSpace: "nowrap" }}
                                        onClick={() => setStudentSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                                        title={M.tooltips.sortByStudent}
                                    >
                                        {M.table.student} <span style={sortIconStyle}>{studentSortGlyph}</span>
                                    </th>
                                    {sortedAssignmentsForApply.map((a) => {
                                        const ruleCode = assignmentRules[a.id] ?? "";
                                        const ruleLabel = getRuleLabel(ruleCode);
                                        return (
                                            <th
                                                key={a.id}
                                                style={{ position: "sticky", top: 0, zIndex: 2, background: "#f9fafb", borderBottom: "1px solid #e5e7eb", minWidth: 260, padding: "8px 10px", textAlign: "left", whiteSpace: "nowrap", cursor: "pointer" }}
                                                onClick={() => setAssignmentColSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                                                title={M.tooltips.sortByAssignment}
                                            >
                                                <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {a.title}
                                                </div>
                                                <div style={{ fontSize: 11, color: "#374151", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {ruleLabel || M.penalty.dash}
                                                </div>
                                                <div style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {formatDue(colByContentId[a.id]?.due, localeTag) || ""}
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                                </thead>
                                <tbody>
                                {sortedStudents.map((s) => {
                                    const name = formatStudentName(s, M);
                                    return (
                                        <tr key={s.userId || s.id}>
                                            <td style={{ position: "sticky", left: 0, zIndex: 3, background: "#fff", boxShadow: "2px 0 0 #e5e7eb", padding: "8px 10px", borderBottom: "1px solid #f3f4f6", whiteSpace: "nowrap" }}>
                                                <div style={{ fontWeight: 600 }}>{name}</div>
                                            </td>
                                            {sortedAssignmentsForApply.map((a) => {
                                                const colMeta = colByContentId[a.id] || {};
                                                const attempt = (attemptsBy[s.userId] || {})[a.id] || null;
                                                const ruleCode = assignmentRules[a.id] ?? "";
                                                const ruleLabel = getRuleLabel(ruleCode);

                                                const base = computePenalty(colMeta?.possible, colMeta?.due, attempt?.attemptDate, ruleLabel);
                                                const ovPts = overridesByUser?.[s.userId]?.[a.id];
                                                const penalty = withOverride(base, ovPts);

                                                const busyKey = `${s.userId}:${a.id}`;
                                                const isBusy = !!overridesBusy[busyKey];
                                                const inputId = `ov-${s.userId}-${a.id}`;

                                                return (
                                                    <td key={a.id} style={{ padding: "8px 10px", borderBottom: "1px solid #f3f4f6", verticalAlign: "middle", whiteSpace: "nowrap" }} title={`${name} × ${a.title}`}>
                                                        {attempt ? (
                                                            <div style={{ fontSize: 12, lineHeight: 1.35 }}>
                                                                <div><strong>{M.attemptCard.attempt}</strong> {formatDue(attempt.attemptDate, localeTag)}</div>
                                                                <div><strong>{M.attemptCard.status}</strong> {attempt.status || M.penalty.dash}</div>
                                                                <div>
                                                                    <strong>{M.attemptCard.penalty}</strong> {formatPenalty(penalty, M)}
                                                                    {typeof ovPts === "number" && <span style={overrideChip}>{M.chips.overridden}</span>}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div style={{ fontSize: 12, lineHeight: 1.35, color: "#6b7280" }}>
                                                                <div><em>{M.attemptCard.noAttempt}</em></div>
                                                                <div><strong>{M.meta.due}</strong> {formatDue(colMeta?.due, localeTag)}</div>
                                                                <div><strong>{M.attemptCard.penalty}</strong> {M.penalty.dash}</div>
                                                            </div>
                                                        )}

                                                        {/* Override controls */}
                                                        <div style={{ marginTop: 6, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                                                            <label htmlFor={inputId} style={{ fontSize: 11 }}>{M.attemptCard.overrideLabel}</label>
                                                            <input
                                                                id={inputId}
                                                                type="number"
                                                                min={0}
                                                                step="0.01"
                                                                defaultValue={typeof ovPts === "number" ? ovPts : (penalty?.computed ?? 0)}
                                                                style={{ width: 90, padding: "4px 6px", border: "1px solid #e5e7eb", borderRadius: 6 }}
                                                                aria-label={`${M.attemptCard.overrideLabel} ${name} / ${a.title}`}
                                                            />
                                                            <button
                                                                type="button"
                                                                disabled={isBusy}
                                                                onClick={() => {
                                                                    const el = document.getElementById(inputId);
                                                                    const raw = el ? el.value : "";
                                                                    const val = Number(raw);
                                                                    if (!Number.isFinite(val) || val < 0) {
                                                                        notify(M.errors.enterNonNegative);
                                                                        return;
                                                                    }
                                                                    handleSaveOverride(a.id, s.userId, val);
                                                                }}
                                                                style={{ padding: "4px 8px", borderRadius: 6, background: "#111827", color: "#fff", fontSize: 12 }}
                                                            >
                                                                {isBusy ? M.buttons.saving : M.buttons.save}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                disabled={isBusy || typeof ovPts !== "number"}
                                                                onClick={() => handleClearOverride(a.id, s.userId)}
                                                                style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid #e5e7eb", background: "#fff", color: "#111827", fontSize: 12 }}
                                                            >
                                                                {M.buttons.clear}
                                                            </button>
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {(attemptsError || columnsError) && (
                        <div style={{ padding: 12 }}>
                            {attemptsError && <div style={{ color: "#b91c1c", fontSize: 12 }}>{M.footerErrors.attempts} {attemptsError}</div>}
                            {columnsError && <div style={{ color: "#b91c1c", fontSize: 12 }}>{M.footerErrors.columns} {columnsError}</div>}
                        </div>
                    )}

                    {syncMsg && <div style={{ padding: 12 }}><div style={toastStyle} role="status" aria-live="polite">{syncMsg}</div></div>}
                </section>
            )}
        </div>
    );

    /** ---- local helpers that need component scope ---- */
    function computeTotalPenaltyForStudent(userId) {
        let total = 0;
        for (const a of assignments) {
            const ruleCode = assignmentRules[a.id] ?? "";
            const ruleLabel = getRuleLabel(ruleCode);
            const colMeta = colByContentId[a.id] || {};
            const attempt = (attemptsBy[userId] || {})[a.id] || null;
            const base = computePenalty(colMeta?.possible, colMeta?.due, attempt?.attemptDate, ruleLabel);
            const ovPts = overridesByUser?.[userId]?.[a.id];
            const p = (typeof ovPts === "number")
                ? { applies: ovPts > 0, computed: round2(ovPts) }
                : base;
            if (p?.applies && Number.isFinite(p.computed)) total += Number(p.computed) || 0;
        }
        return round2(total);
    }
};

/** ---------------- Helpers (pure) ---------------- */
function studentHeaderCell(label, key, { studentViewSortKey, studentViewSortDir, setStudentViewSortKey, setStudentViewSortDir }) {
    const active = studentViewSortKey === key;
    const glyph = !active ? "↕" : studentViewSortDir === "asc" ? "▲" : "▼";
    return (
        <th
            style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
            onClick={() => {
                if (active) {
                    setStudentViewSortDir((d) => (d === "asc" ? "desc" : "asc"));
                } else {
                    setStudentViewSortKey(key);
                    setStudentViewSortDir("asc");
                }
            }}
            title={`Sort by ${label}`}
        >
            {label} <span style={{ fontSize: 12, opacity: 0.6, marginLeft: 6 }}>{glyph}</span>
        </th>
    );
}

function formatDue(iso, localeTag = "en-US") {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "—";
        return d.toLocaleString(localeTag, {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit"
        });
    } catch {
        return "—";
    }
}

function formatStudentName(s, M) {
    const given = (s?.given || "").trim();
    const family = (s?.family || "").trim();
    const dn = (s?.displayName || "").trim();
    if (given || family) return `${given} ${family}`.trim();
    if (dn) return dn;
    return M.names.unnamed;
}

async function fetchAllContents(api, courseId) {
    const results = [];
    const pageThroughLocal = async (path, params = {}) => {
        let offset = 0;
        const limit = 100;
        while (true) {
            const res = await api.get(path, { params: { ...params, limit, offset } });
            const rows = Array.isArray(res.data?.results) ? res.data.results : [];
            results.push(...rows);
            if (rows.length < limit) break;
            offset += limit;
        }
    };
    try {
        await pageThroughLocal(`/courses/${encodeURIComponent(courseId)}/contents`);
    } catch {
        return results;
    }
    return results;
}

/** Fetch all course memberships */
async function fetchAllCourseUsers(apiV1, courseId, { expandUser = true } = {}) {
    const collectPaged = async (path) => {
        const out = [];
        let offset = 0;
        const limit = 100;
        while (true) {
            const params = {
                limit,
                offset,
                ...(expandUser ? { expand: "user" } : {}),
                fields: "id,userId,courseRoleId,availability,user",
            };
            const res = await apiV1.get(path, { params });
            const rows = Array.isArray(res?.data?.results) ? res.data.results : [];
            out.push(...rows);
            if (rows.length < limit) break;
            offset += limit;
        }
        return out;
    };

    try {
        const rows = await collectPaged(`/courses/${encodeURIComponent(courseId)}/users`);
        if (rows.length) return rows;
    } catch { /* fall through */ }

    const rows2 = await collectPaged(`/courses/${encodeURIComponent(courseId)}/memberships`);
    return rows2;
}

/** Best-effort batched user lookups to enrich names when expand didn't return them. */
async function bestEffortUserLookups(apiV1, userIds = []) {
    const uniq = Array.from(new Set(userIds.filter(Boolean)));
    const out = [];
    const pool = 5;
    let i = 0;
    async function next() {
        if (i >= uniq.length) return;
        const id = uniq[i++];
        try {
            const res = await apiV1.get(`/users/${encodeURIComponent(id)}`, { params: { fields: "id,name,displayName" } });
            if (res?.data) out.push(res.data);
        } catch {
            /* ignore */
        }
        return next();
    }
    const workers = Array.from({ length: Math.min(pool, uniq.length) }, () => next());
    await Promise.all(workers);
    return out;
}

/** find or create the "Late Penalties" column with points possible = 0
 *  includes gradebookCategoryId when provided.
 */
async function getOrCreateLatePenaltiesColumn(apiV2, courseId, gradebookCategoryId) {
    const params = { fields: "id,name,score,gradebookCategoryId" };
    const res = await apiV2.get(`/courses/${encodeURIComponent(courseId)}/gradebook/columns`, { params });
    const rows = Array.isArray(res?.data?.results) ? res.data.results : [];
    const existing = rows.find(
        (c) => (c?.name || "").trim().toLowerCase() === "late penalties"
    );
    if (existing?.id) return existing.id;

    const createBody = {
        name: "Late Penalties",
        score: { possible: 0 },
        availability: { available: "Yes" },
        ...(gradebookCategoryId ? { gradebookCategoryId } : {}),
    };
    const createRes = await apiV2.post(
        `/courses/${encodeURIComponent(courseId)}/gradebook/columns`,
        createBody
    );
    return createRes?.data?.id;
}

/** ---------------- Penalty parsing & computation ---------------- */
function parseRuleLabel(label = "") {
    const txt = String(label).toLowerCase().trim();

    // 1) "<X>% penalty if more than <N> <hours|days> late"
    let m = txt.match(/(\d+(?:\.\d+)?)\s*%\s*penalty\s*if\s*more\s*than\s*(\d+(?:\.\d+)?)\s*(hour|hours|day|days)\s*late/);
    if (m) {
        return { type: "percent", amount: parseFloat(m[1]), threshold: parseFloat(m[2]), unit: m[3].startsWith("hour") ? "hours" : "days" };
    }

    // 2) "<X> points penalty if more than <N> <hours|days> late"
    m = txt.match(/(\d+(?:\.\d+)?)\s*points?\s*penalty\s*if\s*more\s*than\s*(\d+(?:\.\d+)?)\s*(hour|hours|day|days)\s*late/);
    if (m) {
        return { type: "points", amount: parseFloat(m[1]), threshold: parseFloat(m[2]), unit: m[3].startsWith("hour") ? "hours" : "days" };
    }

    // 3) "<X>% penalty each day late"
    m = txt.match(/(\d+(?:\.\d+)?)\s*%\s*penalty\s*each\s*day\s*late/);
    if (m) return { type: "percent", amount: parseFloat(m[1]), threshold: 0, unit: "days" };

    // 4) "<X> points penalty each day late" (optional cap)
    m = txt.match(
        /(\d+(?:\.\d+)?)\s*points?\s*penalty\s*each\s*day\s*late(?:\s*(?:for\s*a\s*(?:max|maximum)\s*of|max(?:imum)?)\s*(\d+(?:\.\d+)?)\s*points?)?/
    );
    if (m) return { type: "points_per_day", rate: parseFloat(m[1]), max: m[2] ? parseFloat(m[2]) : null };

    return null;
}

function computePenalty(possible, dueIso, attemptIso, ruleLabel) {
    if (!ruleLabel || !possible || !dueIso || !attemptIso) return null;

    const spec = parseRuleLabel(ruleLabel);
    if (!spec) return null;

    const due = new Date(dueIso);
    const when = new Date(attemptIso);
    if (Number.isNaN(due.getTime()) || Number.isNaN(when.getTime())) return null;

    const lateMs = when.getTime() - due.getTime();
    if (lateMs <= 0) return { applies: false, type: spec.type, amount: 0, computed: 0 };

    const unitMs = spec.unit === "hours" ? 3600_000 : 86400_000;
    const thresholdMs = Number.isFinite(spec.threshold) ? spec.threshold * unitMs : 0;

    if (spec.type === "points_per_day") {
        const daysLate = Math.ceil(lateMs / 86400_000);
        let computed = round2((spec.rate || 0) * daysLate);
        if (Number.isFinite(spec.max)) computed = Math.min(computed, round2(spec.max));
        return { applies: computed > 0, type: "points_per_day", rate: spec.rate, max: spec.max ?? null, computed };
    }

    if (lateMs <= thresholdMs) {
        return { applies: false, type: spec.type, amount: spec.amount, computed: 0 };
    }

    if (spec.type === "percent") {
        const computed = round2((possible * spec.amount) / 100);
        return { applies: true, type: "percent", amount: spec.amount, computed };
    }
    if (spec.type === "points") {
        const computed = round2(spec.amount);
        return { applies: true, type: "points", amount: spec.amount, computed };
    }
    return null;
}

function formatPenalty(p, M) {
    if (!p) return M.penalty.dash;
    if (!p.applies) return M.penalty.none;
    if (p.type === "percent") return `-${p.computed} ${M.penalty.pts} (${p.amount}%)`;
    if (p.type === "points") return `-${p.computed} ${M.penalty.pts}`;
    if (p.type === "points_per_day") return `-${p.computed} ${M.penalty.pts}`;
    if (p.type === "override") return `-${p.computed} ${M.penalty.pts} (${M.penalty.override})`;
    if (p.overridden) return `-${p.computed} ${M.penalty.pts} (${M.penalty.override})`;
    return M.penalty.dash;
}

function round2(n) {
    return Math.round((Number(n) || 0) * 100) / 100;
}

/** ---------------- Styles ---------------- */
const tabsRowStyle = {
    display: "flex",
    gap: 8,
    marginBottom: 12,
    borderBottom: "1px solid #e5e7eb",
    paddingBottom: 4,
};
const tabBtn = {
    padding: "8px 12px",
    borderRadius: "8px 8px 0 0",
    fontWeight: 600,
    cursor: "pointer",
    border: "1px solid #e5e7eb",
    borderBottom: "none",
    background: "#fff",
    color: "#111827",
};
const tabBtnActive = {
    background: "#111827",
    color: "#fff",
    borderColor: "#111827",
};

const cardStyle = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 16, marginBottom: 16 };
const h2Style = { fontSize: 18, fontWeight: "bold", margin: "0 0 12px" };
const listTableStyle = { width: "100%", borderCollapse: "collapse" };
const selectStyle = { width: "100%", padding: "6px 8px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff" };
const primaryBtn = { padding: "6px 12px", borderRadius: 8, background: "#111827", color: "#fff" };
const secondaryBtn = { padding: "6px 12px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", color: "#111827" };
const dangerBtn = { padding: "6px 12px", border: "1px solid #dc2626", borderRadius: 8, background: "#fff", color: "#dc2626" };
const toastStyle = { margin: "8px 0", padding: "8px", background: "#ecfdf5", color: "#065f46", borderRadius: 6 };
const iconBtn = {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: "10px",
    lineHeight: "1",
    color: "#b91c1c",
    padding: 0,
    margin: 0
};
const linkBtn = {
    border: "none",
    background: "transparent",
    padding: 0,
    color: "#2563eb",
    cursor: "pointer",
    textDecoration: "underline"
};
const sortIconStyle = { fontSize: 12, opacity: 0.6, marginLeft: 6 };
const overrideChip = {
    marginLeft: 8,
    padding: "2px 6px",
    fontSize: 10,
    borderRadius: 999,
    background: "#fef3c7",
    color: "#92400e",
    border: "1px solid #fcd34d",
};

export default LatePenaltyCourseDetails;

